import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { pluck } from 'underscore';
import { Button, Divider, Text, makeStyles, mergeClasses, shorthands, tokens } from '@fluentui/react-components';
import { Drawer, DrawerBody, DrawerHeader, DrawerHeaderTitle, DrawerFooter } from '@fluentui/react-drawer';
import { MessageBar, MessageBarBody, MessageBarTitle } from '@fluentui/react-message-bar';
import { DismissRegular } from '@fluentui/react-icons';
import { UiBlocker } from '@bingads-webui-react/ui-blocker';
import { getBulkApiErrors } from '@bingads-webui-campaign/api-errors';
import { useMultiSelectContext } from '@bingads-webui-campaign-react/multi-selector';
import { ValidatedAdditionalConversionInput } from './additional-conversion-input-section';
import { AudienceListSection } from './audience-list-section';
import { createOrUpdateNewCustomerAcquisitionGoal } from '../data-source';
import { ADDITIONAL_CONVERSION_VALUE_TYPES } from '../constants';

export const useCustomerAcquisitionSetupStyle = makeStyles({
  rootContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  bodyContainer: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('16px'),
    '& .error-text': {
      color: tokens.colorPaletteRedForeground1,
    },
  },
  footerContainer: {
    position: 'absolute',
    bottom: '0px',
    left: '0px',
  },
  messageBar: {
    '& .fui-MessageBar__icon': {
      marginRight: '12px',
    },
    '& .fui-MessageBarBody': {
      whiteSpace: 'normal',
      paddingTop: '8px',
      paddingBottom: '8px',
    },
  },
});

const getConversionValueOption = (CampaignSettings) => {
  if (CampaignSettings && CampaignSettings.additionalConversionValue) {
    return ADDITIONAL_CONVERSION_VALUE_TYPES.CAMPAIGN;
  }
  return ADDITIONAL_CONVERSION_VALUE_TYPES.ACCOUNT;
};

export const CustomerAcquisitionSetup = (props) => {
  const {
    newCustomerAcquisition, isCampaignCreation, isAccountLevel, cid, aid, i18n,
    onChange: onChangeFromProp, isEditorOpen, onClose,
  } = props;
  const { accountSettings, campaignSettings } = newCustomerAcquisition || {};
  const styles = useCustomerAcquisitionSetupStyle();
  const { selectedOptions } = useMultiSelectContext();
  const selectedOptionKeys = useMemo(() => pluck(selectedOptions, 'key'), [selectedOptions]);
  const [accountBudgetInput, setAccountBudgetInput] = useState(accountSettings ? accountSettings.additionalConversionValue : null);
  const [campaignBudgetInput, setCampaignBudgetInput] = useState(campaignSettings ? campaignSettings.additionalConversionValue : null);
  const [conversionValueOption, setConversionValueOption] = useState(getConversionValueOption(campaignSettings));
  const [isUpdating, setIsUpdating] = useState(false);
  const [userErrors, setUserErrors] = useState([]);
  const [serverErrors, setServerErrors] = useState([]);
  const [shouldDisableSave, setShouldDisableSave] = useState(false);

  // Effect to determine if the save button should be disabled based on
  // the current option and validation errors
  useEffect(() => {
    if (selectedOptionKeys.length === 0) {
      // Always disable if no audience is selected
      setShouldDisableSave(true);
      return;
    }

    if (isAccountLevel) {
      const hasAccountBudgetError = !accountBudgetInput || userErrors.length > 0;
      setShouldDisableSave(hasAccountBudgetError);
    } else if (conversionValueOption === ADDITIONAL_CONVERSION_VALUE_TYPES.ACCOUNT) {
      // Campaign panel - use account-level option
      // No validation needed for account-level option in campaign context
      setShouldDisableSave(false);
    } else {
      // Campaign panel - use campaign override option
      // For campaign-level option, validate campaign budget
      const hasCampaignBudgetError = !campaignBudgetInput || userErrors.length > 0;
      setShouldDisableSave(hasCampaignBudgetError);
    }
  }, [isAccountLevel, conversionValueOption, userErrors, accountBudgetInput, campaignBudgetInput, selectedOptionKeys.length]);


  const onValidateBudgetInput = useCallback((result) => {
    const { data, messages } = result;
    const newAccountBudget = data ? data['account-setting'] : null;
    setAccountBudgetInput(newAccountBudget);
    const newCampaignBudget = data ? data['campaign-setting'] : null;
    setCampaignBudgetInput(newCampaignBudget);
    setUserErrors(messages);
  }, []);

  const onSave = useCallback(() => {
    if (!isAccountLevel) {
      onChangeFromProp({
        newCustomerAcquisition: {
          ...newCustomerAcquisition,
          campaignSettings: {
            ...newCustomerAcquisition.campaignSettings,
            additionalConversionValue: conversionValueOption === ADDITIONAL_CONVERSION_VALUE_TYPES.CAMPAIGN && campaignBudgetInput !== null ? parseFloat(campaignBudgetInput) : null,
          },
        },
      });
      onClose();
      return;
    }

    const payload = {
      NewCustomerAcquisitionGoals: [
        {
          Id: accountSettings && accountSettings.goalId ? accountSettings.goalId : -1,
          AudienceIds: selectedOptionKeys.join(';'),
          AdditionalConversionValue: parseFloat(accountBudgetInput),
        },
      ],
    };
    const saveCallPromise = createOrUpdateNewCustomerAcquisitionGoal({ cid, aid });
    setIsUpdating(true);
    saveCallPromise(payload)
      .then((result) => {
        if (result && result.NewCustomerAcquisitionGoals && result.NewCustomerAcquisitionGoals.length > 0) {
          const audienceIdList = result.NewCustomerAcquisitionGoals[0].AudienceIds.split(';');
          const newAccountSettings = {
            goalId: result.NewCustomerAcquisitionGoals[0].Id,
            additionalConversionValue: result.NewCustomerAcquisitionGoals[0].AdditionalConversionValue,
            audiences: selectedOptions.filter(option => audienceIdList.includes(option.key.toString())).map(option => ({
              Id: option.key,
              Name: option.text,
              Type: option.type,
            })),
          };
          onChangeFromProp({
            newCustomerAcquisition: {
              ...newCustomerAcquisition,
              accountSettings: newAccountSettings,
            },
          });
          setServerErrors([]);
        }
        onClose();
      })
      .catch((e) => {
        if (e && e.errors && e.errors.length > 0) {
          setServerErrors(getBulkApiErrors(e.errors, 'New Customer Acquisition Goal', i18n));
        }
      })
      .finally(() => {
        setIsUpdating(false);
      });
  }, [accountBudgetInput, accountSettings, aid, campaignBudgetInput, cid, conversionValueOption, i18n, isAccountLevel, newCustomerAcquisition, onChangeFromProp, onClose, selectedOptionKeys, selectedOptions]);

  return (
    <Drawer
      className={mergeClasses(styles.rootContainer, 'customer-acquisition-setup-panel')}
      size="medium"
      position="end"
      type="overlay"
      modalType="modal"
      open={isEditorOpen}
      onOpenChange={onClose}
    >
      <UiBlocker blocking={isUpdating} loadingText={i18n.getString(_TL_('Updating...'))}>
        <DrawerHeader>
          <DrawerHeaderTitle
            action={
              <Button
                appearance="subtle"
                aria-label="Close"
                icon={<DismissRegular />}
                onClick={onClose}
              />
            }
          >
            {i18n.getString(_TL_('Set up new customer acquisition'))}
          </DrawerHeaderTitle>
        </DrawerHeader>
        <DrawerBody className={styles.bodyContainer}>
          { serverErrors.map(error => (
            <Text className="error-text" key={error.code} size={300}>{ error.message }</Text>
          )) }
          { isCampaignCreation && isAccountLevel && (
            <MessageBar
              className={styles.messageBar}
              intent="info"
              layout="singleline"
            >
              <MessageBarBody>
                <MessageBarTitle>{ i18n.getString(_TL_('Account-level settings: '))}</MessageBarTitle>
                <span>{i18n.getString(_TL_('The additional conversion value and existing customer lists you set will be automatically saved at the account level. To make changes after saving, manage them on the Conversion Goals page.'))}</span>
              </MessageBarBody>
            </MessageBar>
          )}
          <ValidatedAdditionalConversionInput
            i18n={i18n}
            isAccountLevel={isAccountLevel}
            accountAdditionalConversionValue={accountSettings ? accountSettings.additionalConversionValue : null}
            conversionValueOption={conversionValueOption}
            setConversionValueOption={setConversionValueOption}
            validationProps={{
              onValidate: onValidateBudgetInput,
              data: {
                'account-setting': accountBudgetInput,
                'campaign-setting': campaignBudgetInput,
              },
            }}
          />
          <Divider />
          <AudienceListSection
            i18n={i18n}
            isAccountLevel={isAccountLevel}
            cid={cid}
            aid={aid}
          />
        </DrawerBody>
        <DrawerFooter className={styles.footerContainer}>
          <Button className="apply-button" appearance="primary" onClick={onSave} disabled={shouldDisableSave}>
            {i18n.getString(_TL_('Save'))}
          </Button>
          <Button className="cancel-button" onClick={onClose}>
            {i18n.getString(_TL_('Cancel'))}
          </Button>
        </DrawerFooter>
      </UiBlocker>
    </Drawer>
  );
};

CustomerAcquisitionSetup.propTypes = {
  newCustomerAcquisition: PropTypes.shape({
    isEnabled: PropTypes.bool,
    campaignSettings: PropTypes.shape({
      additionalConversionValue: PropTypes.number,
      bidOnlyMode: PropTypes.bool,
      goalId: PropTypes.number,
    }),
    accountSettings: PropTypes.shape({
      goalId: PropTypes.number,
      additionalConversionValue: PropTypes.number,
      audiences: PropTypes.arrayOf({
        Id: PropTypes.string,
        Name: PropTypes.string,
      }),
    }),
  }).isRequired,
  isCampaignCreation: PropTypes.bool.isRequired,
  isAccountLevel: PropTypes.bool.isRequired,
  cid: PropTypes.number.isRequired,
  aid: PropTypes.number.isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  isEditorOpen: PropTypes.bool.isRequired,
};

